import { useEffect, useMemo, useState } from "react";
import {
  Copy, ExternalLink, CheckCircle2, Clock, MessageSquare,
  BookOpen, Compass, Home as HomeIcon, X as XIcon, Sparkles, Filter, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/**
 * GMBPostStudio — the human-in-the-loop GMB publish flow.
 *
 * Why not fully automate? Google's Business Profile API `localPosts.create`
 * requires per-merchant OAuth allow-listing that takes 2-8 weeks and is
 * routinely rejected for solo practices. A copy-paste flow gives the same
 * SEO recency signal with zero approval risk — the human clicks "Post" in
 * GMB within seconds of copying.
 */

const KIND_META = {
  blog:  { label: "Blog",     Icon: BookOpen,  color: "text-blue-500" },
  hub:   { label: "Content",  Icon: Compass,   color: "text-emerald-500" },
  guide: { label: "Guide",    Icon: HomeIcon,  color: "text-amber-500" },
};

const STATUS_META = {
  pending:   { label: "Pending",  className: "bg-amber-500/12 text-amber-600 border-amber-500/30" },
  published: { label: "Published on GMB", className: "bg-emerald-500/12 text-emerald-600 border-emerald-500/30" },
  skipped:   { label: "Skipped",  className: "bg-muted text-muted-foreground border-border" },
};

const GMB_DASHBOARD = "https://business.google.com/";

const GMBPostStudio = () => {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [preview, setPreview] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/gmb/drafts", { params: { limit: 60 } });
      setDrafts(r.data.drafts || []);
    } catch {
      toast.error("Failed to load GMB drafts");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(
    () => (filter === "all" ? drafts : drafts.filter((d) => d.status === filter)),
    [drafts, filter]
  );

  const counts = useMemo(() => ({
    all: drafts.length,
    pending: drafts.filter((d) => d.status === "pending").length,
    published: drafts.filter((d) => d.status === "published").length,
    skipped: drafts.filter((d) => d.status === "skipped").length,
  }), [drafts]);

  const setStatus = async (d, next_status, opts = {}) => {
    try {
      await api.post(`/admin/gmb/${d.kind}/${d.slug}/status`, { status: next_status });
      setDrafts((cur) => cur.map((x) =>
        (x.kind === d.kind && x.slug === d.slug)
          ? { ...x, status: next_status, published_at: next_status === "published" ? new Date().toISOString() : x.published_at }
          : x
      ));
      if (!opts.silent) {
        toast.success(next_status === "published" ? "Marked as published ✓" : "Status updated");
      }
    } catch {
      toast.error("Could not update status");
    }
  };

  const copyAndOpen = async (d) => {
    try {
      await navigator.clipboard.writeText(d.body);
    } catch {
      toast.error("Clipboard blocked — please copy manually from the preview panel");
      setPreview(d);
      return;
    }
    // Bookkeeping — record the copy so we can nudge later.
    api.post(`/admin/gmb/${d.kind}/${d.slug}/copied`).catch(() => {});
    // Show a persistent toast with the next steps because the GMB tab will
    // steal focus in a moment.
    toast(
      (t) => (
        <div className="text-xs leading-relaxed">
          <div className="font-semibold text-[hsl(var(--foreground))] mb-1">Post text copied ✓</div>
          <ol className="list-decimal ml-4 text-muted-foreground space-y-0.5">
            <li>GMB opened in a new tab — click <b>Add update</b></li>
            <li>Paste (⌘V / Ctrl-V)</li>
            <li>Add the hero image if not auto-pulled</li>
            <li>Set CTA to <b>Learn more</b> → <code>{d.cta_url}</code></li>
            <li>Click <b>Post</b>, then come back and <b>Mark as published</b></li>
          </ol>
        </div>
      ),
      { duration: 14000 }
    );
    window.open(GMB_DASHBOARD, "_blank", "noopener,noreferrer");
  };

  return (
    <div data-testid="gmb-studio-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div className="max-w-2xl">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent))]">
            <Sparkles size={12} /> GMB Post Studio
          </div>
          <h2 className="mt-2 font-serif text-2xl sm:text-3xl leading-tight">
            One click. One paste. Fresh Google post.
          </h2>
          <p className="mt-2 text-[13.5px] text-muted-foreground leading-relaxed">
            Every blog / hub / guide you publish shows up here as a ready-to-paste GMB draft
            (headline + 1500-char body + CTA link).
            Click <b>Copy &amp; Open GMB</b>, paste, hit Post — under 15 seconds.
            A fresh GMB post every 3-5 days is Google's #2 local-pack recency signal (right after reviews).
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="gmb-refresh-btn"
          className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-md border border-border hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] transition-colors"
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <Filter size={13} className="text-muted-foreground" />
        {["pending", "published", "skipped", "all"].map((k) => {
          const active = filter === k;
          return (
            <button
              key={k}
              type="button"
              onClick={() => setFilter(k)}
              data-testid={`gmb-filter-${k}`}
              className={`text-[11.5px] px-3 py-1.5 rounded-full border transition-colors
                ${active
                  ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]"
                  : "border-border text-muted-foreground hover:border-[hsl(var(--foreground))]/40"}`}
            >
              {k[0].toUpperCase() + k.slice(1)} · {counts[k]}
            </button>
          );
        })}
      </div>

      {loading && (
        <div className="text-sm text-muted-foreground py-10 text-center">Loading drafts…</div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="text-sm text-muted-foreground border border-dashed border-border rounded-xl p-10 text-center">
          Nothing to show in <b>{filter}</b>. Great news — you're up to date on GMB.
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="grid gap-3">
          {filtered.map((d) => {
            const kind = KIND_META[d.kind] || KIND_META.blog;
            const status = STATUS_META[d.status] || STATUS_META.pending;
            const KindIcon = kind.Icon;
            return (
              <div
                key={`${d.kind}:${d.slug}`}
                data-testid={`gmb-row-${d.kind}-${d.slug}`}
                className="rounded-xl border border-border p-4 sm:p-5 flex gap-4 items-start hover:border-[hsl(var(--primary))]/40 transition-colors"
              >
                {d.hero_image ? (
                  <img
                    src={d.hero_image}
                    alt=""
                    loading="lazy"
                    className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-lg border border-border shrink-0"
                  />
                ) : (
                  <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg bg-muted border border-border shrink-0 flex items-center justify-center">
                    <KindIcon size={22} className="text-muted-foreground" />
                  </div>
                )}

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap text-[11px] uppercase tracking-[0.18em]">
                    <span className={`inline-flex items-center gap-1 ${kind.color}`}>
                      <KindIcon size={11} /> {kind.label}
                    </span>
                    <span className={`px-1.5 py-0.5 rounded-full border ${status.className}`}>
                      {status.label}
                    </span>
                    <span className="text-muted-foreground/70">
                      {d.char_count}/1500 chars
                    </span>
                    {d.copied_at && d.status === "pending" && (
                      <span className="inline-flex items-center gap-1 text-amber-600" title="Copied but not marked published">
                        <Clock size={11} /> copied — did you post it?
                      </span>
                    )}
                  </div>
                  <div className="mt-2 font-medium text-[15px] leading-snug line-clamp-2">{d.title}</div>
                  <div className="mt-1.5 text-[13px] text-muted-foreground line-clamp-2 leading-snug">
                    {d.body.split("\n\n")[0]}
                  </div>

                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => copyAndOpen(d)}
                      data-testid={`gmb-copy-btn-${d.slug}`}
                      className="inline-flex items-center gap-1.5 text-[11.5px] px-3 py-1.5 rounded-md bg-[hsl(var(--primary))] text-white hover:opacity-90 transition-opacity"
                    >
                      <Copy size={12} /> Copy &amp; Open GMB
                    </button>
                    <button
                      type="button"
                      onClick={() => setPreview(d)}
                      data-testid={`gmb-preview-btn-${d.slug}`}
                      className="inline-flex items-center gap-1.5 text-[11.5px] px-3 py-1.5 rounded-md border border-border hover:border-[hsl(var(--foreground))]/40 transition-colors"
                    >
                      <MessageSquare size={12} /> Preview
                    </button>
                    <a
                      href={d.cta_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-[11.5px] px-3 py-1.5 rounded-md border border-border hover:border-[hsl(var(--foreground))]/40 transition-colors"
                    >
                      <ExternalLink size={12} /> Open live URL
                    </a>
                    {d.status !== "published" ? (
                      <button
                        type="button"
                        onClick={() => setStatus(d, "published")}
                        data-testid={`gmb-mark-published-${d.slug}`}
                        className="inline-flex items-center gap-1.5 text-[11.5px] px-3 py-1.5 rounded-md border border-emerald-500/40 text-emerald-600 hover:bg-emerald-500/10 transition-colors ml-auto"
                      >
                        <CheckCircle2 size={12} /> Mark as published
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setStatus(d, "pending")}
                        data-testid={`gmb-reopen-${d.slug}`}
                        className="text-[11px] text-muted-foreground hover:text-destructive transition-colors ml-auto"
                      >
                        Reopen
                      </button>
                    )}
                    {d.status === "pending" && (
                      <button
                        type="button"
                        onClick={() => setStatus(d, "skipped")}
                        data-testid={`gmb-skip-${d.slug}`}
                        className="text-[11px] text-muted-foreground hover:text-destructive transition-colors"
                      >
                        Skip
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Preview modal */}
      {preview && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setPreview(null)}
          data-testid="gmb-preview-modal"
        >
          <div
            className="bg-background border border-border rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-background">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent))]">GMB Post Preview</div>
                <div className="mt-1 font-medium text-sm">This is exactly what patients will see on Google.</div>
              </div>
              <button
                type="button"
                onClick={() => setPreview(null)}
                data-testid="gmb-preview-close"
                className="p-1.5 rounded-full hover:bg-muted"
              >
                <XIcon size={16} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {preview.hero_image && (
                <img src={preview.hero_image} alt="Post hero" className="w-full aspect-[4/3] object-cover rounded-lg border border-border" />
              )}
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-1">Headline (60 char)</div>
                <div className="font-medium text-[15px]">{preview.title}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-1">Body ({preview.char_count}/1500 char)</div>
                <textarea
                  readOnly
                  className="w-full text-[13px] leading-relaxed p-3 rounded-lg border border-border bg-muted/30 font-mono resize-none"
                  rows={12}
                  value={preview.body}
                  onFocus={(e) => e.target.select()}
                  data-testid="gmb-preview-body"
                />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-1">CTA Button</div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border text-[12px]">
                  <span className="font-medium">{preview.cta_label}</span>
                  <span className="text-muted-foreground">→</span>
                  <a href={preview.cta_url} target="_blank" rel="noopener noreferrer" className="text-[hsl(var(--primary))] underline">
                    {preview.cta_url}
                  </a>
                </div>
              </div>
              <div className="pt-3 border-t border-border flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => { copyAndOpen(preview); setPreview(null); }}
                  data-testid="gmb-modal-copy-btn"
                  className="btn-primary !px-4 !py-2 !text-xs"
                >
                  <Copy size={13} /> Copy &amp; Open GMB
                </button>
                <button
                  type="button"
                  onClick={() => { setStatus(preview, "published"); setPreview(null); }}
                  className="inline-flex items-center gap-1.5 text-[12px] px-3 py-2 rounded-md border border-emerald-500/40 text-emerald-600 hover:bg-emerald-500/10 transition-colors"
                >
                  <CheckCircle2 size={13} /> Mark as published
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GMBPostStudio;
